import { RootContext } from "@/contexts/rootContext";
import { PLAN_STEPS } from "@/types/plan";
import { DESIRE_RELATED_PATHS, NAVBAR_LINK } from "@/utils/enum";
import { getGlobalState, setBackButtonNavigation } from "@/utils/routeUtils";
import { useRouter } from "next/router";
import { useCallback, useContext, useMemo } from "react";

interface UseBackOptions {
  customPath?: string;
  skipSpecialCases?: boolean;
}

const isPathIncluded = (path: string, searchPath: string): boolean =>
  path.includes(searchPath);

export const useBack = (options: UseBackOptions = {}) => {
  const { state } = useContext(RootContext);
  const router = useRouter();

  const globalState = getGlobalState();

  const userUid = globalState?.user?.uid || state.user?.uid;
  const isLoggedIn = globalState?.isLoggedIn || state.isLoggedIn;
  const prevRoute = globalState?.prevRoute || state.prevRoute;
  const skipSpecialCases = options.skipSpecialCases;

  const handleSpecialCases = useCallback(
    (currentPath: string, lastPath: string): boolean => {
      if (skipSpecialCases) return false;
      // Special case 1: desireSetting → desire → userWoman/[uid]
      if (
        isPathIncluded(currentPath, NAVBAR_LINK.desireSetting) &&
        isPathIncluded(lastPath, NAVBAR_LINK.desire) &&
        userUid
      ) {
        router.push(`${NAVBAR_LINK.userWoman}/${userUid}`);
        return true;
      }

      // Special case 2: userWoman page from desireSetting/desire → home
      if (
        DESIRE_RELATED_PATHS.includes(lastPath) &&
        isPathIncluded(currentPath, NAVBAR_LINK.userWoman)
      ) {
        router.push(NAVBAR_LINK.home);
        return true;
      }

      if (
        (isPathIncluded(currentPath, NAVBAR_LINK.desireSetting) &&
          isPathIncluded(lastPath, NAVBAR_LINK.profileSetting)) ||
        (isPathIncluded(lastPath, NAVBAR_LINK.desireSetting) &&
          isPathIncluded(currentPath, NAVBAR_LINK.profileSetting))
      ) {
        router.push(NAVBAR_LINK.home);
        return true;
      }
      if (
        isPathIncluded(lastPath, currentPath) ||
        isPathIncluded(currentPath, lastPath)
      ) {
        // Special case 3: lastPath include currentPath -> home
        router.push(NAVBAR_LINK.home);
        return true;
      }

      return false;
    },
    [router, userUid]
  );

  const handleSignInNavigation = useCallback(
    (lastPath: string): boolean => {
      if (!isPathIncluded(lastPath, NAVBAR_LINK.signIn)) return false;

      if (isLoggedIn) {
        router.push(NAVBAR_LINK.home);
      } else {
        // Move DOM access outside of useCallback to prevent dependency issues
        const urlParams = new URLSearchParams(
          typeof window !== "undefined" ? window.location.search : ""
        );
        const returnUrl = urlParams.get("returnUrl");
        const signInUrl = returnUrl
          ? `${NAVBAR_LINK.signIn}?returnUrl=${returnUrl}`
          : NAVBAR_LINK.signIn;
        router.push(signInUrl);
      }
      return true;
    },
    [isLoggedIn, router]
  );

  const handleDefaultNavigation = useCallback((): void => {
    const fallbackUrl = isLoggedIn ? NAVBAR_LINK.home : NAVBAR_LINK.defaultPage;
    router.push(fallbackUrl);
  }, [isLoggedIn]);

  const goBack = useCallback(
    (customPath?: string) => {
      setBackButtonNavigation(true);

      // Handle custom path
      const targetPath = customPath || options.customPath;
      if (targetPath) {
        router.push(targetPath);
        return;
      }

      const lastPath = prevRoute || NAVBAR_LINK.home;
      const currentPath = router.asPath;

      // Handle navigation with previous route
      if (lastPath) {
        // Handle special navigation cases
        if (handleSpecialCases(currentPath, lastPath)) return;

        // Handle signIn page navigation
        if (handleSignInNavigation(lastPath)) return;

        if (currentPath === NAVBAR_LINK.plans) {
          const prev = history.state.step;
          if (prev === PLAN_STEPS.payment) {
            return router.back();
          }
        }

        // Normal case: use browser back
        // router.back();
        router.push(lastPath);
        return;
      }

      // No previous route: use fallback
      handleDefaultNavigation();
    },
    [
      prevRoute,
      router,
      handleDefaultNavigation,
      handleSpecialCases,
      handleSignInNavigation
    ]
  );

  // Memoize the return object to prevent unnecessary re-renders
  const returnValue = useMemo(
    () => ({
      goBack
    }),
    [goBack]
  );

  return returnValue;
};
